import React, { useEffect, useState } from "react";
import { api, fmtMoney } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import PageHeader from "@/components/PageHeader";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Crown, Check } from "lucide-react";
import { toast } from "sonner";

export default function Premium() {
  const { user } = useAuth();
  const [pkgs, setPkgs] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api.get("/payments/packages").then((r) => setPkgs(r.data)).catch(() => {});
  }, []);

  const upgrade = async (pkg_id) => {
    setLoading(true);
    try {
      const { data } = await api.post("/payments/checkout", {
        package_id: pkg_id,
        origin_url: window.location.origin,
      });
      window.location.href = data.url;
    } catch (e) {
      toast.error("Couldn't start checkout. Try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="page-premium">
      <PageHeader eyebrow="Premium" title="Upgrade to LoyaltyCircle Premium" subtitle="Bonus cashback, AI travel concierge, family vault sharing." />

      {user?.premium && (
        <Card className="border-emerald-100 shadow-none rounded-2xl gradient-emerald mb-6">
          <CardContent className="p-6 flex items-center gap-3">
            <Crown className="w-6 h-6 text-emerald-700" />
            <div>
              <div className="font-display font-bold text-lg">You're a Premium member</div>
              <div className="text-sm text-gray-700">Enjoy 10% bonus cashback on every purchase.</div>
            </div>
          </CardContent>
        </Card>
      )}

      <div className="grid sm:grid-cols-2 gap-6">
        {pkgs.map((p) => (
          <Card key={p.id} className="border-gray-200 shadow-none rounded-2xl">
            <CardContent className="p-6">
              <div className="text-xs uppercase tracking-[0.18em] text-amber-700 font-semibold">{p.id.includes("yearly") ? "Best value" : "Flexible"}</div>
              <h3 className="font-display font-bold text-2xl mt-1">{p.label}</h3>
              <div className="mt-4 flex items-baseline gap-1">
                <span className="font-display font-bold text-4xl">{fmtMoney(p.amount)}</span>
                <span className="text-gray-500">/{p.id.includes("yearly") ? "year" : "month"}</span>
              </div>
              <ul className="mt-5 space-y-2 text-sm text-gray-700">
                {p.perks.map((x, i) => (
                  <li key={i} className="flex items-start gap-2"><Check className="w-4 h-4 text-emerald-700 mt-0.5" />{x}</li>
                ))}
              </ul>
              <Button
                onClick={() => upgrade(p.id)}
                disabled={loading || user?.premium}
                className="mt-6 w-full bg-emerald-700 hover:bg-emerald-800 text-white rounded-full"
                data-testid={`buy-${p.id}`}
              >
                <Crown className="w-4 h-4 mr-2" /> {user?.premium ? "Already premium" : `Upgrade now`}
              </Button>
            </CardContent>
          </Card>
        ))}
      </div>

      <p className="mt-6 text-xs text-gray-500">Powered by Stripe. Secure payment. Cancel anytime.</p>
    </div>
  );
}
